// src/components/Navbar.js
'use client';
import React, { useState, useEffect, useContext, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import styles from '../styles/navbar.module.css';
import supabase from '@/lib/supabase';
import { UserContext } from '../context/UserContext';
import { Menu, Search } from 'lucide-react';
import SearchBar from '@/components/SearchBar';
import ForTutorsMenu from '@/components/ForTutors/ForTutorsMenu';

export default function Navbar({ allCourses }) {
  const pathname = usePathname();
  const user = useContext(UserContext);

  // ─── Learner‐dropdown state (for USER role) ────────────────────
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [classesDropdownVisible, setClassesDropdownVisible] = useState(false);
  const classesDropdownRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (classesDropdownRef.current && !classesDropdownRef.current.contains(e.target)) {
        setClassesDropdownVisible(false);
      }
    };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, []);

  useEffect(() => {
    if (classesDropdownVisible && user?.id && user.role === 'USER') {
      setLoadingStudents(true);
      fetch('/api/learners')
        .then(res => res.json())
        .then(data => setEnrolledStudents(data?.error ? [] : (data || [])))
        .catch(() => setEnrolledStudents([]))
        .finally(() => setLoadingStudents(false));
    }
  }, [classesDropdownVisible, user]);

  // ─── Hydration guard ──────────────────────────────────────────
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // ─── Mobile & settings menu state ──────────────────────────────
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [settingsMenuVisible, setSettingsMenuVisible] = useState(false);
  const settingsMenuRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleMobileMenu = () => setMobileMenuVisible(v => !v);
  const toggleClassesDropdown = (e) => {
    e.stopPropagation();
    setClassesDropdownVisible(v => !v);
  };
  const toggleSettingsMenu = () => setSettingsMenuVisible(v => !v);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  const getProfileLink = () => {
    if (!user?.id) return '/';
    return user.role === 'TEACHER'
      ? `/teacher-profile/${user.id}`
      : `/public-profile/${user.id}`;
  };

  return (
    <header className={styles.siteHeader}>
      <div className={styles.container}>

        {/* Branding */}
        <Link href="/" className={styles.logo}>
          <span className={styles.logoText}>PenguinLearn</span>
        </Link>

        {/* SearchBar (desktop) / Catalog link (mobile) */}
        {mounted ? (
          isMobile ? (
            <Link
              href="/catalog"
              className={styles.navLink}
              aria-label="Go to catalog"
            >
              Catalog <Search size={16} />
            </Link>
          ) : (
            <SearchBar allCourses={allCourses} />
          )
        ) : (
          <SearchBar allCourses={allCourses} />
        )}

        {/* Hamburger toggle (mobile) */}
        <div className={styles.hamburgerMenu}>
          <button
            onClick={toggleMobileMenu}
            className={styles.hamburgerButton}
            aria-label="Toggle menu"
          >
            {mobileMenuVisible ? '✖' : '☰'}
          </button>
        </div>

        {/* Main navigation */}
        <nav className={`${styles.mainNav} ${mobileMenuVisible ? styles.mobileVisible : ''}`}>
          <ul className={styles.navList}>

            <li>
              <Link
                href="/"
                className={`${styles.navLink} ${pathname === '/' ? styles.active : ''}`}
              >
                Home
              </Link>
            </li>

            <li>
              <Link
                href="/catalog"
                className={`${styles.navLink} ${pathname === '/catalog' ? styles.active : ''}`}
              >
                Catalog
              </Link>
            </li>

            {mounted ? (
              user ? (
                <>
                  {user.role === 'SUPER_ADMIN' && (
                    <li>
                      <Link
                        href="/manage-users"
                        className={`${styles.navLink} ${pathname === '/manage-users' ? styles.active : ''}`}
                      >
                        Manage Users
                      </Link>
                    </li>
                  )}

                  {(user.role === 'SUPER_ADMIN' || user.role === 'ADMIN') && (
                    <li>
                      <Link
                        href="/approval-page"
                        className={`${styles.navLink} ${pathname === '/approval-page' ? styles.active : ''}`}
                      >
                        Pending Courses
                      </Link>
                    </li>
                  )}

                  {(user.role === 'SUPER_ADMIN' || user.role === 'ADMIN') && (
                    <li>
                      <Link
                        href="/manage-reviews"
                        className={`${styles.navLink} ${pathname === '/manage-reviews' ? styles.active : ''}`}
                      >
                        Manage Reviews
                      </Link>
                    </li>
                  )}

                  {user.role === 'USER' && (
                    <li className={styles.dropdown} ref={classesDropdownRef}>
                      <button onClick={toggleClassesDropdown} className={styles.navLink}>
                        Switch to{' '}
                        <span className={`${styles.arrow} ${classesDropdownVisible ? styles.open : ''}`}>
                          ▾
                        </span>
                      </button>
                      {classesDropdownVisible && (
                        <ul className={styles.dropdownMenu}>
                          {loadingStudents ? (
                            <li className={styles.dropdownItem}>Loading…</li>
                          ) : enrolledStudents.length > 0 ? (
                            enrolledStudents.map((stu) => (
                              <li key={stu.id}>
                                <Link
                                  href={`/student-page/${stu.id}`}
                                  className={styles.dropdownItem}
                                >
                                  {stu.name}
                                </Link>
                              </li>
                            ))
                          ) : (
                            <li className={styles.dropdownItem}>No learners found</li>
                          )}
                        </ul>
                      )}
                    </li>
                  )}

                  {user.role === 'TEACHER' && (
                    <>
                      <li>
                        <Link
                          href="/schedule"
                          className={`${styles.navLink} ${pathname === '/schedule' ? styles.active : ''}`}
                        >
                          Schedule
                        </Link>
                      </li>

                      <li className={styles.navLink}>
                        <ForTutorsMenu />
                      </li>
                    </>
                  )}

                  {(user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') && (
                    <>
                      <li>
                        <Link
                          href="/my-classes"
                          className={`${styles.navLink} ${pathname === '/my-classes' ? styles.active : ''}`}
                        >
                          My Classes
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/transaction-log"
                          className={`${styles.navLink} ${pathname === '/transaction-log' ? styles.active : ''}`}
                        >
                          Transaction Log
                        </Link>
                      </li>
                    </>
                  )}

                  <li>
                    <Link
                      href="/chats"
                      className={`${styles.navLink} ${pathname === '/chats' ? styles.active : ''}`}
                    >
                      Chats
                    </Link>
                  </li>

                  {/* Settings & Logout */}
                  {isMobile ? (
                    <>
                      <li>
                        <Link
                          href={getProfileLink()}
                          className={styles.navLink}
                          onClick={toggleMobileMenu}
                        >
                          My Profile
                        </Link>
                      </li>
                      <li>
                        <Link href="/settings" className={styles.navLink}>
                          Settings
                        </Link>
                      </li>
                      <li>
                        <button onClick={handleLogout} className={styles.navLink}>
                          Logout
                        </button>
                      </li>
                    </>
                  ) : (
                    <li className={styles.dropdown} ref={settingsMenuRef}>
                      <button onClick={toggleSettingsMenu} className={styles.navLink}>
                        <Menu size={20} />
                      </button>
                      {settingsMenuVisible && (
                        <ul className={styles.dropdownMenu}>
                          <li>
                            <Link href={getProfileLink()} className={styles.dropdownItem}>
                              My Profile
                            </Link>
                          </li>
                          <li>
                            <button
                              onClick={() => (window.location.href = '/settings')}
                              className={styles.dropdownItem}
                            >
                              Settings
                            </button>
                          </li>
                          <li>
                            <button onClick={handleLogout} className={styles.dropdownItem}>
                              Logout
                            </button>
                          </li>
                        </ul>
                      )}
                    </li>
                  )}
                </>
              ) : (
                <>
                  <li>
                    <Link
                      href="/login"
                      className={`${styles.loginButton} ${pathname === '/login' ? styles.activeButton : ''}`}
                    >
                      Login
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/register"
                      className={`${styles.registerButton} ${pathname === '/register' ? styles.activeButton : ''}`}
                    >
                      Register
                    </Link>
                  </li>
                </>
              )
            ) : null}
          </ul>
        </nav>

      </div>
    </header>
  );
}
